/**
 * CoConnect Route AI material-movement policy.
 *
 * Purpose:
 * - suppress GPS micro-jitter from causing route-engine churn;
 * - invalidate a single-stop plan only when displacement is materially greater
 *   than positioning uncertainty;
 * - keep route distance/ETA authority in the deterministic road engine.
 *
 * Policy:
 * - minimum material displacement: 100 m;
 * - uncertainty gate: 3 × the larger valid accuracy radius from plan/current fix;
 * - because Driver GPS fixes >100 m accuracy are already rejected upstream,
 *   the normal threshold is 100–300 m.
 */
export const ROUTE_AI_REPLAN_MIN_MOVEMENT_M = 100;

export interface RouteAiOriginSnapshot {
  lat: number;
  lng: number;
  accuracyM: number | null;
}

function validAccuracy(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function routeAiMovementMeters(
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number
): number {
  const R = 6371000;
  const rad = (x: number) => x * Math.PI / 180;
  const dLat = rad(toLat - fromLat);
  const dLng = rad(toLng - fromLng);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(rad(fromLat)) * Math.cos(rad(toLat)) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function routeAiReplanThresholdMeters(
  baselineAccuracyM: unknown,
  currentAccuracyM: unknown
): number {
  const baseline = validAccuracy(baselineAccuracyM) ?? 0;
  const current = validAccuracy(currentAccuracyM) ?? 0;
  return Math.max(ROUTE_AI_REPLAN_MIN_MOVEMENT_M, 3 * Math.max(baseline, current));
}

export function routeAiMovementDecision(
  baseline: RouteAiOriginSnapshot,
  current: RouteAiOriginSnapshot
): {
  movedMeters: number;
  thresholdMeters: number;
  shouldReplan: boolean;
} {
  const movedMeters = routeAiMovementMeters(
    baseline.lat,
    baseline.lng,
    current.lat,
    current.lng
  );
  const thresholdMeters = routeAiReplanThresholdMeters(
    baseline.accuracyM,
    current.accuracyM
  );

  return {
    movedMeters,
    thresholdMeters,
    shouldReplan: movedMeters >= thresholdMeters,
  };
}
